/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { BundleType } from '../resources/nls/bundle';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { sortControlStyles } from './themes/SortControl.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { IconButton } from '../UNSAFE_IconButton';
import { EmbeddedIconButton } from '../PRIVATE_EmbeddedIconButton';
import { SortAscendingIcon } from '../PRIVATE_ThemedIcons/SortAscendingIcon';
import { SortDescendingIcon } from '../PRIVATE_ThemedIcons/SortDescendingIcon';
import { useSortableContext } from '#hooks/UNSAFE_useSortableContext';

/**
 * Props for the SortControl Component
 */
export type Props = TestIdProps;

/**
 * Component that renders an interactive sort control.
 */
export const SortControl = ({ testId }: Props) => {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const testIdProps = useTestId(testId);

  const { isRendered, sortable, sortDirection, onSort, isTabbable } = useSortableContext();

  const iconClassArray = [sortControlStyles.base];
  if (isRendered) {
    iconClassArray.push(sortControlStyles.visible);
  } else {
    iconClassArray.push(sortControlStyles.hidden);
  }
  const iconClasses = classNames(iconClassArray);

  return (
    <div {...testIdProps} class={iconClasses}>
      {isTabbable ? (
        <IconButton
          aria-label={
            sortable === 'enabled'
              ? translations.collection_sortEnabled()
              : translations.collection_sortDisabled()
          }
          isDisabled={sortable !== 'enabled'}
          onAction={onSort}
          size="xs"
          variant="ghost">
          {sortDirection === 'descending' ? <SortDescendingIcon /> : <SortAscendingIcon />}
        </IconButton>
      ) : (
        <EmbeddedIconButton
          aria-label={
            sortable === 'enabled'
              ? translations.collection_sortEnabled()
              : translations.collection_sortDisabled()
          }
          isDisabled={sortable !== 'enabled'}
          onAction={onSort}>
          {sortDirection === 'descending' ? <SortDescendingIcon /> : <SortAscendingIcon />}
        </EmbeddedIconButton>
      )}
    </div>
  );
};
